import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  Injector,
  Signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {LetDirective} from '@ngrx/component';
import {Observable} from 'rxjs';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeChipModule} from 'se-fe-chip';
import {SeFeEmptyStateModule} from 'se-fe-empty-state';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeHeaderModule} from 'se-fe-header';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeMenuModule, SeFeMenuOptions} from 'se-fe-menu';
import {SeFeModalModule} from 'se-fe-modal';
import {SeFePageModule} from 'se-fe-page';
import {SeFePositionModule, SeFePositionService} from 'se-fe-position';
import {SeFeToastModule} from 'se-fe-toast';
import {ItemDetailsCardComponent} from '../../../../common/components/item-details-card/item-details-card.component';
import {ItemMediaCardComponent} from '../../../../common/components/item-media-card/item-media-card.component';
import {ItemOrgCardComponent} from '../../../../common/components/item-org-card/item-org-card.component';
import {FullPageSpinnerComponent} from '../../../../common/components/full-page-spinner/full-page-spinner.component';
import {ProductDetailVariantsComponent} from './product-detail-variants/product-detail-variants.component';
import {ProductBundlingCardComponent} from './product-bundling-card/product-bundling-card.component';
import {ProductSpecsModalComponent} from './product-specs-modal/product-specs-modal.component';
import {SpecsAndVariants} from '../../../../models';
import {Product} from '../../../../models/clare';
import {
  ProductDetailStore,
  ProductDetailVm,
  ProductImagesUpdate,
  ProductOrganizationUpdate,
  UpdateProductDetail,
} from './product-detail.store';
import {SeFeOverlayModule} from 'se-fe-overlay';
import {InventoryVariation} from '../../../../models/clare';
import {ProductInventoryModalComponent} from './product-inventory-modal/product-inventory-modal.component';

interface ChipState {
  style: 'caution' | 'done' | 'off';
  text: string;
  disabled: boolean;
}

interface WarningData {
  header: string;
  subheader: string;
}

const initialVm: ProductDetailVm = {
  product: undefined,
  variants: [],
  specs: [],
  loading: true,
  image: {src: '', alt: ''},
  images: [],
  vendorName: '',
  error: undefined,
  backLink: {backLink: '', backLinkParams: {}},
  allVariantsDisabled: true,
  price: 0,
  onlySoldInBundles: false,
  onlySoldInBundlesUpdating: false,
  brandName: undefined,
  bundles: [],
  catalogs: [],
  catalogsLoading: false,
  organizationCatalogs: [],
  organizationCatalogsLoading: false,
  organizationId: '',
};

/**
 * Component for displaying a selected product's details.
 * @Component
 */
@Component({
  selector: 'product-detail',
  imports: [
    ItemDetailsCardComponent,
    ItemMediaCardComponent,
    ItemOrgCardComponent,
    FullPageSpinnerComponent,
    LetDirective,
    ProductBundlingCardComponent,
    ProductDetailVariantsComponent,
    ProductSpecsModalComponent,
    ProductInventoryModalComponent,
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeChipModule,
    SeFeEmptyStateModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeIconModule,
    SeFeMenuModule,
    SeFeModalModule,
    SeFeOverlayModule,
    SeFePageModule,
    SeFePositionModule,
    SeFeToastModule,
  ],
  providers: [SeFePositionService, ProductDetailStore],
  templateUrl: './product-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./product-detail.component.scss'],
})
export class ProductDetailComponent implements AfterViewInit {
  specsModal = viewChild<ProductSpecsModalComponent>('specsModalComponent');
  inventoryModal = viewChild<ProductInventoryModalComponent>('inventoryModalComponent');

  variantMenuOptions: SeFeMenuOptions = {
    name: 'Product variant actions',
    sections: [
      {
        menuItems: [
          {text: 'Edit product variants', action: trigger => this.specsModal()?.open(trigger as Event | Element)},
          {text: 'Edit variant inventory', action: trigger => this.inventoryModal()?.open(trigger as Event | Element)},
        ],
      },
    ],
  };

  productStatusMenuOptions: SeFeMenuOptions = {
    name: 'Product status',
    sections: [
      {
        menuItems: [
          {text: 'Active', action: () => this.toggleProductActiveStatus(true)},
          {text: 'Inactive', action: () => this.toggleProductActiveStatus(false)},
        ],
      },
    ],
  };
  vm$: Observable<ProductDetailVm>;
  error$: Observable<any>;

  vm: Signal<ProductDetailVm>;
  chipState: Signal<ChipState | null>;
  warningData: Signal<WarningData[]>;

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private injector: Injector,
    private productDetailStore: ProductDetailStore
  ) {
    this.vm$ = this.productDetailStore.vm$;
    this.error$ = this.productDetailStore.error$;

    this.vm = toSignal(this.productDetailStore.vm$, {initialValue: initialVm, injector: this.injector});

    this.chipState = computed(() => {
      const currentVm = this.vm();
      const disabled = currentVm.variants.length === 0 || currentVm.allVariantsDisabled;

      if (currentVm.variants.length === 0) return {style: 'off', text: 'Inactive', disabled};

      const isActive = !!currentVm.product?.active;
      return isActive ? {style: 'done', text: 'Active', disabled} : {style: 'off', text: 'Inactive', disabled};
    });

    this.warningData = computed(() => {
      const currentVm = this.vm();
      const warnings: WarningData[] = [];
      if (currentVm.product?.active === false) {
        warnings.push({
          header: 'Product is not active',
          subheader: `This product currently has an inactive status. In order for this product to show up in any Registrations, it
            must first be set to active. If you have disabled all variants, you must first enable at least one variant
            to change the product's status to active.`,
        });
      }
      if (currentVm.variants.length === 0) {
        warnings.push({
          header: 'There are no variants for this product',
          subheader: 'You must create at least one variant in order to activate this product.',
        });
      }
      return warnings;
    });

    this.productDetailStore.init();
  }

  async ngAfterViewInit(): Promise<void> {
    this.cdr.detectChanges();
  }

  /**
   * Toggles the product active status using the component store.
   * @param active - The new active status.
   */
  toggleProductActiveStatus(active: boolean): void {
    this.productDetailStore.updateProductStatus(active);
  }

  /**
   * Updates the variant status using the component store.
   * @param event - The update event containing the new variant status.
   */
  updateVariantStatus(event: {id: string; active: boolean}): void {
    this.productDetailStore.updateVariantStatus(event);
  }

  updateVariantInventory(variants: InventoryVariation[]): void {
    this.productDetailStore.updateVariantInventory(variants);
  }

  /**
   * Updates the specs using the component store.
   * @param event - The update event containing the new specs.
   */
  updateSpecs(event: UpdateProductDetail<SpecsAndVariants>): void {
    this.productDetailStore.updateSpecsAndVariants(event);
  }

  /**
   * Updates the product and price break using the component store.
   * @param event - The update event containing the new product and price break.
   */
  updateProductAndPriceBreak(event: UpdateProductDetail<{product: Product}>): void {
    this.productDetailStore.updateProductAndPriceBreak(event);
  }

  updateOnlySoldInBundles(onlySoldInBundles: boolean): void {
    this.productDetailStore.updateOnlySoldInBundles(onlySoldInBundles);
  }

  /**
   * Updates the product images using the component store.
   * @param event - The update event containing the images array.
   */
  updateProductImageAndDescription(event: UpdateProductDetail<ProductImagesUpdate>): void {
    this.productDetailStore.updateProductImageAndDescription(event);
  }
  /**
   * Sets the error state in the component store.
   * @param event - An object containing the error message or null.
   */
  setError(event: {error: string | undefined}): void {
    this.productDetailStore.setError(event.error);
  }

  /**
   * Updates the product organization fields using the component store.
   * @param event - The update event containing the new product organization values.
   */
  saveProductOrganization(event: UpdateProductDetail<ProductOrganizationUpdate>): void {
    this.productDetailStore.saveProductOrganization(event);
  }

  loadOrganizationCatalogs(): void {
    this.productDetailStore.loadOrganizationCatalogs();
  }

  openSpecsModal(): void {
    const button = this.elementRef.nativeElement.querySelector('.variant-empty-state__button');
    this.specsModal()?.open(button);
  }
}
